import { useState, useEffect } from "react";
import { useUser } from "@clerk/react";
import { X, PartyPopper, MessageSquare, Sparkles } from "lucide-react";

export default function PilotWelcomeBanner() {
  const { user } = useUser();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem("pilot_welcome_dismissed");
    if (!dismissed) setVisible(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem("pilot_welcome_dismissed", "true");
    setVisible(false);
  };

  if (!visible) return null;

  const firstName = user?.firstName;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative animate-in fade-in zoom-in duration-300">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-slate-400" />
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-teal-50 text-teal-600 mb-4">
            <PartyPopper className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">
            {firstName ? `Welcome aboard, ${firstName}!` : "Welcome aboard!"}
          </h2>
          <p className="text-slate-600 mt-2 text-sm leading-relaxed">
            You're one of the first clinicians to use RheumHub. This platform is being
            shaped by people like you — and your feedback will directly influence what we build next.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex gap-3 p-3 rounded-xl bg-teal-50">
            <Sparkles className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-teal-900">Things will evolve</p>
              <p className="text-xs text-teal-700 mt-0.5">
                Features may change based on feedback — that's the point of a pilot.
              </p>
            </div>
          </div>
          <div className="flex gap-3 p-3 rounded-xl bg-amber-50">
            <MessageSquare className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Your voice matters</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Something broken? Confusing? Tell us. We prioritise fixes that make your day easier.
              </p>
            </div>
          </div>
        </div>

        <div className="border-l-3 border-teal-500 pl-4 mb-6">
          <p className="text-sm text-slate-600 italic leading-relaxed">
            "I built RheumHub because I saw how much time clinicians lose navigating fragmented
            tools. This pilot is how we fix that — together."
          </p>
          <p className="text-xs text-slate-400 mt-1">— Yahya Hameed, Founder</p>
        </div>

        <button
          onClick={dismiss}
          className="w-full py-3 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 transition-colors"
        >
          Let's get started
        </button>
      </div>
    </div>
  );
}
